/**
 * GraphQL Endpoint Config — Direct API endpoint constants
 *
 * All GraphQL calls go directly to the backend API.
 * Client attaches auth tokens and API key via Apollo links.
 *
 * Two backend targets:
 *   - 'gateway': API Gateway (main GraphQL federation — catalog, CMS, auth, etc.)
 *   - 'commerce': Commerce backend direct (cart, checkout, payment — bypasses gateway)
 *
 * Usage in modules:
 *   import { GRAPHQL_PROXY_ENDPOINTS, useGraphQlEndpoint } from '@kidslabs/module-core';
 *   const uri = useGraphQlEndpoint('commerce');
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type GraphQlTarget = 'gateway' | 'commerce';

// ─── API Endpoints ────────────────────────────────────────────────────────────

/**
 * Get GraphQL endpoints — server-aware (direct API on both client and server).
 * On server, uses internal server URLs; on client, uses public URLs.
 */
function getEndpoints(): Record<GraphQlTarget, string> {
    const isServer = typeof window === 'undefined';
    return {
        gateway: isServer
            ? process.env.GATEWAY_API_ENDPOINT_SERVER || process.env.NEXT_PUBLIC_GATEWAY_API_ENDPOINT || '/graphql'
            : process.env.NEXT_PUBLIC_GATEWAY_API_ENDPOINT || '/graphql',
        commerce: isServer
            ? process.env.COMMERCE_GATEWAY_API_ENDPOINT_SERVER ||
              process.env.NEXT_PUBLIC_MAGENTO_GATEWAY_API_ENDPOINT ||
              '/graphql'
            : process.env.NEXT_PUBLIC_MAGENTO_GATEWAY_API_ENDPOINT || '/graphql',
    };
}

/** @deprecated Use getEndpoints() for server-aware URLs */
export const GRAPHQL_PROXY_ENDPOINTS: Record<GraphQlTarget, string> = {
    get gateway() {
        return getEndpoints().gateway;
    },
    get commerce() {
        return getEndpoints().commerce;
    },
} as Record<GraphQlTarget, string>;

// ─── React Hook ───────────────────────────────────────────────────────────────

/**
 * Hook to get the GraphQL proxy endpoint URL for a given target.
 *
 * @param target - 'gateway' (API Gateway) or 'commerce' (Commerce direct)
 * @returns The proxy endpoint URL
 *
 * @example
 *   // For API Gateway (default)
 *   const uri = useGraphQlEndpoint();
 *
 *   // For Commerce direct (cart, checkout)
 *   const uri = useGraphQlEndpoint('commerce');
 */
export function useGraphQlEndpoint(target: GraphQlTarget = 'gateway'): string {
    return getEndpoints()[target];
}
